import React, { useMemo, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useJournal } from '../context/JournalContext';
import { computeStats, equityCurve, groupByCategory, groupByMonth, groupByWeek } from '../lib/calc';
import { StatCard } from '../components/dashboard/StatCard';
import { ChartCard } from '../components/dashboard/ChartCard';
import { BucketBarChart, EmptyChart, tooltipStyle } from '../components/dashboard/BucketBarChart';
import { fmtPct } from '../lib/cn';
import { fmtMoneyEs, fmtPctEs } from '../lib/format';
import { useDisplayMode } from '../context/DisplayModeContext';
import { ConfluencePanel } from '../components/dashboard/ConfluencePanel';
import { PendingReviewCard } from '../components/dashboard/PendingReviewCard';
export function DashboardPage() {
  const {
    state,
    activeOperations,
    activeJournals,
    categoriesSorted
  } = useJournal();
  const {
    mode,
    currency
  } = useDisplayMode();
  const [planFilter, setPlanFilter] = useState<'all' | 'followed' | 'broken'>('all');
  const [reviewFilter, setReviewFilter] = useState<'all' | 'pending' | 'reviewed' | 'unmarked'>('all');
  const reviewByDate = useMemo(() => {
    const map: Record<string, string> = {};
    activeJournals.forEach(journal => {
      map[journal.date] = journal.reviewStatus ?? 'none';
    });
    return map;
  }, [activeJournals]);
  const ops = useMemo(() => activeOperations.filter(op => {
    if (planFilter === 'followed' && op.followedPlan !== true) return false;
    if (planFilter === 'broken' && op.followedPlan !== false) return false;
    if (reviewFilter !== 'all') {
      const status = reviewByDate[op.date] ?? 'none';
      if (reviewFilter === 'pending' && status !== 'pending') return false;
      if (reviewFilter === 'reviewed' && status !== 'reviewed') return false;
      if (reviewFilter === 'unmarked' && status !== 'none') return false;
    }
    return true;
  }), [activeOperations, planFilter, reviewFilter, reviewByDate]);
  const balanceMoney = useMemo(() => ops.reduce((sum, op) => sum + (op.resultMoney ?? 0), 0), [ops]);
  const stats = useMemo(() => computeStats(ops), [ops]);
  const curve = useMemo(() => equityCurve(ops), [ops]);
  const byMonth = useMemo(() => groupByMonth(ops), [ops]);
  const byWeek = useMemo(() => groupByWeek(ops), [ops]);

  // Charts are built from whatever categories the user has, so a brand new
  // category shows up automatically without any code change.
  const categoryCharts = useMemo(() => categoriesSorted.map(category => ({
    category,
    data: groupByCategory(ops, category.id, state.tags)
  })), [categoriesSorted, ops, state.tags]);
  const empty = ops.length === 0;
  return <div className="mx-auto max-w-6xl" data-id="element-655" data-mp="1tht5li7tr3uy">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3" data-id="element-656" data-mp="1tgri2e9mgowc">
        <div data-id="element-657" data-mp="1yigt3twuh2kf">
          <h1 className="text-xl font-semibold tracking-tight" data-id="element-658" data-mp="1yi8jcaw0qddg">Dashboard</h1>
          <p className="mt-0.5 text-[13px] text-ink-soft" data-id="element-659" data-mp="1qx4vnm6qxkta">
            Métricas calculadas a partir de tus operaciones registradas.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-660" data-mp="1m15zhcf6eyib">
          <label className="flex items-center gap-1.5" data-id="element-661" data-mp="1zxb2wqbupok0">
            <span className="text-[12px] text-ink-soft" data-id="element-662" data-mp="1uzrit9sh2hk5">Revisión:</span>
            <select value={reviewFilter} onChange={event => setReviewFilter(event.target.value as typeof reviewFilter)} className="h-9 rounded-lg border border-line bg-white px-2.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-accent/25" data-id="element-663" data-mp="1uxf4r7pu4rcd">
              <option value="all" data-id="element-664" data-mp="1h1kzcyo9sk40">Todos</option>
              <option value="pending" data-id="element-665" data-mp="1c7yxm1jna0mz">Pendientes de revisión</option>
              <option value="reviewed" data-id="element-666" data-mp="oj60w2v77orb">Revisados</option>
              <option value="unmarked" data-id="element-667" data-mp="ohmkmc9jldpj">Sin revisar</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5" data-id="element-668" data-mp="jk2twcvnr8gp">
            <span className="text-[12px] text-ink-soft" data-id="element-669" data-mp="x7usw1sb78kj">Plan:</span>
            <select value={planFilter} onChange={event => setPlanFilter(event.target.value as typeof planFilter)} className="h-9 rounded-lg border border-line bg-white px-2.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-accent/25" data-id="element-670" data-mp="x58ouc4mnb72">
              <option value="all" data-id="element-671" data-mp="1fxkzdm5fpisa">Todas</option>
              <option value="followed" data-id="element-672" data-mp="1fwrnk6rmc33a">Solo siguiendo el plan</option>
              <option value="broken" data-id="element-673" data-mp="1kw4lbmk12ukn">Solo fuera del plan</option>
            </select>
          </label>
        </div>
      </div>

      {empty && <div className="mb-5 rounded-2xl border border-dashed border-line bg-surface/40 px-5 py-10 text-center" data-id="element-674" data-mp="1pwijmcvtuq5h">
          <p className="text-sm font-medium text-ink" data-id="element-675" data-mp="1usr3u4ovt9ji">Dashboard vacío</p>
          <p className="mx-auto mt-1 max-w-sm text-[13px] text-ink-soft" data-id="element-676" data-mp="3rof2k7kbr2aa">
            Registra operaciones desde el calendario y aquí verás tu balance,
            win rate, curva de capital y desglose por etiquetas.
          </p>
        </div>}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3" data-id="element-677" data-mp="290m21vdrvj3x">
        <StatCard label="Balance" value={mode === 'money' ? fmtMoneyEs(balanceMoney, true, currency) : fmtPctEs(stats.balancePct)} tone={(mode === 'money' ? balanceMoney : stats.balancePct) >= 0 ? 'pos' : 'neg'} hint={mode === 'money' ? 'Suma de resultados en dinero' : 'Suma de resultados en %'} data-id="element-678" data-mp="28z2l02kal413" />
        <StatCard label="Win Rate" value={`${stats.winRate.toFixed(1)}%`} hint={`${stats.wins}G · ${stats.losses}P · ${stats.breakeven}BE`} data-id="element-679" data-mp="2snthzxmkcwk4" />
        <StatCard label="Profit Factor" value={stats.profitFactor === null ? '—' : stats.profitFactor.toFixed(2)} tone={stats.profitFactor === null ? 'neutral' : stats.profitFactor >= 1 ? 'pos' : 'neg'} data-id="element-680" data-mp="2iq5jqxpr16x7" />
        <StatCard label="RR medio" value={`${stats.avgRR.toFixed(2)}R`} tone={stats.avgRR >= 0 ? 'pos' : 'neg'} data-id="element-681" data-mp="18rnm799ses5v" />
        <StatCard label="Drawdown máx." value={stats.maxDrawdown === 0 ? '—' : `-${stats.maxDrawdown.toFixed(2)}%`} tone={stats.maxDrawdown === 0 ? 'neutral' : 'neg'} data-id="element-682" data-mp="1dp5ztcyr1vk7" />
        <StatCard label="Operaciones" value={String(stats.totalOps)} data-id="element-683" data-mp="14sevyedyhcf2" />
      </div>

      <PendingReviewCard data-id="element-684" data-mp="q0d2bc2hbfnh" />

      <div className="mt-4" data-id="element-685" data-mp="use7ke18c640">
        <ChartCard title="Curva de capital" subtitle="Balance % acumulado" data-id="element-686" data-mp="usnyq4y0gjba">
          {curve.length === 0 ? <EmptyChart data-id="element-687" data-mp="h214aoza9fl0" /> : <ResponsiveContainer width="100%" height={260} data-id="element-688" data-mp="gz6gcnh29cmu">
              <AreaChart data={curve} margin={{
            top: 6,
            right: 8,
            left: -18,
            bottom: 0
          }} data-id="element-689" data-mp="l3e7ssewl6or">
                <defs data-id="element-690" data-mp="756481nd374m">
                  <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1" data-id="element-691" data-mp="c1n0jj4psrss">
                    <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.18} data-id="element-692" data-mp="c3r5m4ecnwun" />
                    <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} data-id="element-693" data-mp="3olr64w8dmxw7" />
                  </linearGradient>
                </defs>
                <XAxis dataKey="index" tick={{
              fontSize: 11,
              fill: '#6b7280'
            }} axisLine={false} tickLine={false} data-id="element-694" data-mp="2easkfca49vb" />
                <YAxis tick={{
              fontSize: 11,
              fill: '#6b7280'
            }} axisLine={false} tickLine={false} width={44} tickFormatter={v => `${v}%`} data-id="element-695" data-mp="bepffj4w5x0x" />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [fmtPct(v), 'Balance']} labelFormatter={l => `Op. ${l}`} data-id="element-696" data-mp="l48yopwinswb" />
                <Area type="monotone" dataKey="equity" stroke="#4f46e5" strokeWidth={2} fill="url(#eq)" data-id="element-697" data-mp="u5qc20ijhghb" />
              </AreaChart>
            </ResponsiveContainer>}
        </ChartCard>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2" data-id="element-698" data-mp="21k0k9pc7ni7f">
        <ChartCard title="Resultados por mes" data-id="element-699" data-mp="26ibw25m2g2sa">
          <BucketBarChart data={byMonth} data-id="element-700" data-mp="2ailzpbmdyh3z" />
        </ChartCard>
        <ChartCard title="Resultados por semana" data-id="element-701" data-mp="2ai2ijx4k7wr0">
          <BucketBarChart data={byWeek} data-id="element-702" data-mp="1dslflpwyoot0" />
        </ChartCard>
        {categoryCharts.map(({
        category,
        data
      }) => <ChartCard key={category.id} title={`Resultados por ${category.name}`} data-id="element-703" data-mp="1iqmzgd27os5j">
            <BucketBarChart data={data} data-id="element-704" data-mp="1mt9qibr2691o" />
          </ChartCard>)}
      </div>

      <ConfluencePanel ops={ops} data-id="element-705" data-mp="1rjq8881wwgtu" />
    </div>;
}